import type { PlaceResult, AddressComponents } from '@/hooks/useGooglePlacesAutocomplete'

export interface ParsedAddress {
  streetNumber: string
  streetName: string
  city: string
  state: string
  zipCode: string
  country: string
  formattedAddress: string
  aptUnit?: string
}

/**
 * Parse Google Places address components into individual fields
 * @param place - Google Places API result
 */
export function parseAddressComponents(
  place: PlaceResult
): ParsedAddress {
  const components = place.address_components || ({} as AddressComponents)
  const formattedAddress = place.formatted_address || ''

  // Extract street number
  const streetNumber = components.street_number || ''

  // Extract route (street name) - use entire route as street name
  const route = components.route || ''
  const streetName = route || ''

  // Extract city - check multiple possible fields
  // Google Places can return city in different fields depending on location
  // For international addresses, check all possible city fields
  const city =
    components.locality ||
    components.neighborhood ||
    components.sublocality ||
    components.sublocality_level_1 ||
    components.postal_town ||
    components.administrative_area_level_2 || // District/County (sometimes used for city)
    ''

  // Extract state/province - check multiple levels for international addresses
  const state = 
    components.administrative_area_level_1 || // State/Province
    components.administrative_area_level_2 || // Sometimes used for state in some countries
    ''
  
  // Extract postal code - works for all countries
  const zipCode = components.postal_code || ''
  
  // Extract country code (2-letter ISO code)
  const country = components.country || ''

  return {
    streetNumber,
    streetName,
    city,
    state,
    zipCode,
    country,
    formattedAddress,
  }
}

/**
 * Extract apartment/unit number from formatted address
 * Looks for patterns like "Apt 123", "Unit 456", "#789", etc.
 */
export function extractAptUnit(formattedAddress: string): string {
  const patterns = [
    /(?:apt|apartment|unit|suite|ste|#)\s*([a-z0-9-]+)/i,
    /#\s*([a-z0-9-]+)/i,
  ]

  for (const pattern of patterns) {
    const match = formattedAddress.match(pattern)
    if (match && match[1]) {
      return match[1]
    }
  }

  return ''
}

